import { defineStore } from "pinia";
import { computed, ref, shallowRef, watch } from "vue";
import { useRouter } from "vue-router";
import { createChatSession } from "@recordsure/unity-agent";

import { getAgentClient } from "@/services/AgentClient";
import { useCaseStore } from "@/stores/case-store";

import type { ChatSession, ChatState, ChatTurn } from "@recordsure/unity-agent";

// set_case's widget, which a successful agent case switch always carries (see UnityAgent's SetCaseUiApp).
const SET_CASE_RESOURCE_URI = "ui://set-case/mcp-app.html";

/** The case a reply's set_case moved the conversation to, if it did. */
function switchedCase(turn: ChatTurn | undefined): string | undefined {
  if (turn?.role !== "assistant") return undefined;
  for (const event of turn.events) {
    if (event.kind !== "resource" || event.resource.uri !== SET_CASE_RESOURCE_URI || event.toolResult.isError) continue;
    const caseReference = (event.toolResult.structuredContent as { caseReference?: unknown } | undefined)
      ?.caseReference;
    if (typeof caseReference === "string" && caseReference.length > 0) return caseReference;
  }
  return undefined;
}

// The tab's one agent chat, kept here rather than in the Agent Assistant panel so it survives the
// panel being closed or the case changing — a tab keeps its session link across cases (see
// case-store's onCaseReferenceLoaded), so the conversation carries on with the new case.
export const useAgentChatStore = defineStore("agentChat", () => {
  const caseStore = useCaseStore();
  const router = useRouter();
  let chat: ChatSession | undefined;
  const state = shallowRef<ChatState>();
  // The case the conversation is on (null: none, on the brand screen) — where the tab last moved the
  // session, or where the agent's set_case took it since; undefined until the tab has moved it anywhere.
  const conversationCase = ref<string | null>();
  // The tab went to the brand screen while the agent stayed on its case, which the next message says.
  let leftForBrandScreen = false;

  /**
   * The case the agent is working on when it isn't the one on screen, e.g. after its set_case while the
   * user drills through scores. Offered as a link rather than followed, so the user isn't moved
   * mid-task.
   */
  const agentCaseElsewhere = computed(() =>
    conversationCase.value != null && conversationCase.value !== (caseStore.selectedCaseName ?? null)
      ? conversationCase.value
      : undefined,
  );

  // Registered here, in the store's own scope, rather than when the chat is first created from a
  // component, so it isn't torn down when that component unmounts.
  watch(
    () => caseStore.sessionLinkId,
    (sessionLinkId) => {
      conversationCase.value = undefined;
      leftForBrandScreen = false;
      chat?.setSessionLink(sessionLinkId, caseStore.sessionLinkResumed);
    },
  );

  /** Created on first use, as only the Agent Assistant panel needs it (and only where config.agent is set). */
  function getChat(): ChatSession {
    if (chat == null) {
      const created = createChatSession(getAgentClient());
      created.subscribe(() => {
        const previous = state.value;
        state.value = created.getState();
        trackAgentCaseSwitch(previous, state.value);
        trackAgentNewChat(previous, state.value);
      });
      created.setSessionLink(caseStore.sessionLinkId, caseStore.sessionLinkResumed);
      state.value = created.getState();
      chat = created;
    }
    return chat;
  }

  /**
   * Called once the session has moved to caseReference (null: off any case, on the brand screen), so
   * the agent's tools act on it. The chat's earlier turns are about where it was before, so the agent
   * is told with the next message. The first place a tab loads is where the conversation already is.
   */
  function noteCaseChange(caseReference: string | null) {
    const previous = conversationCase.value;
    if (previous !== undefined && previous !== caseReference) {
      // File references only resolve inside their own case.
      const hadFiles = (chat?.getState().chips.length ?? 0) > 0;
      chat?.clearChips();
      chat?.noteContextChange(
        (caseReference != null ? `Switched to case ${caseReference}.` : `Left case ${previous}; no case is open.`) +
          (hadFiles ? " File chips cleared." : ""),
      );
    } else if (leftForBrandScreen && previous === caseReference) {
      chat?.noteContextChange(`Back on case ${caseReference}.`);
    }
    leftForBrandScreen = false;
    conversationCase.value = caseReference;
  }

  /**
   * Called when the tab goes to the brand screen with a session already running. The agent keeps the case
   * it is working on (agentCaseElsewhere offers it as a link), and its next message says where the user is.
   */
  function noteBrandScreen() {
    void whenIdle().then(() => {
      if (caseStore.selectedCaseName != null || conversationCase.value == null) return;
      leftForBrandScreen = true;
      chat?.noteContextChange(`Now on the brand screen; the agent is still working on case ${conversationCase.value}.`);
    });
  }

  // Notes where the agent's set_case took the conversation (it needs no note about its own switch),
  // so agentCaseElsewhere can offer it. Only for a reply as it arrives — a restored conversation's
  // history may switch to cases long since left.
  function trackAgentCaseSwitch(previous: ChatState | undefined, current: ChatState) {
    if (current.restoring || previous == null || current.turns.length <= previous.turns.length) return;
    const caseReference = switchedCase(current.turns.at(-1));
    if (caseReference != null) {
      if (caseReference !== conversationCase.value) chat?.clearChips();
      conversationCase.value = caseReference;
    }
  }

  // The agent's start_over on this Viewer session clears nothing server-side; the tab starts a fresh chat
  // itself, once the turn that asked for it has finished. Only for a reply as it arrives, not a restore.
  function trackAgentNewChat(previous: ChatState | undefined, current: ChatState) {
    if (current.restoring || previous == null || current.turns.length <= previous.turns.length) return;
    const turn = current.turns.at(-1);
    if (turn?.role === "assistant" && turn.events.some((event) => event.kind === "new-chat")) {
      caseStore.startNewAgentSession();
    }
  }

  /** Opens the case the agent is working on. */
  function openAgentCase() {
    if (agentCaseElsewhere.value == null) return;
    void router.push({
      name: "case",
      params: { brand: router.currentRoute.value.params.brand, casename: agentCaseElsewhere.value },
    });
  }

  /** Resolves once no turn (or restore) is in flight — immediately if there's no chat. */
  function whenIdle(): Promise<void> {
    const current = chat;
    if (current?.getState().sending !== true) return Promise.resolve();
    return new Promise((resolve) => {
      const unsubscribe = current.subscribe(() => {
        if (current.getState().sending) return;
        unsubscribe();
        resolve();
      });
    });
  }

  /** The case the agent is working on, if known (null: none; undefined: not known, e.g. after a reload). */
  const agentCase = computed(() => conversationCase.value);

  return { state, agentCase, agentCaseElsewhere, getChat, whenIdle, noteCaseChange, noteBrandScreen, openAgentCase };
});
